// The checkout's sections, shared by the drawer (CartPanel's CheckoutView)
// and the full-page stepped checkout (CheckoutPage): the booker's contact
// fields, Special requests, the spa treatments' "Who is each treatment for?",
// the totals with codes and gift cards, the consents, the co-host notice and
// the notes under Pay. Moved verbatim from CheckoutView: each reads the one
// useCheckoutSubmit() state it is handed, so the two surfaces show and charge
// the same thing. Presentational only; no state of their own.
import { useEffect, useRef } from "react";
import { LoyaltyBanner } from "../LoyaltyBanner";
import { RedeemPointsControl } from "../RedeemPointsControl";
import { normalizePhoneE164 } from "../../lib/phone";
import { cohostNoticeText } from "../../state/cohost";
import type { AppliedCode } from "../../state/booking";
import { offersBookAnother, payBlockedByGuard, watchesEarlierPayment } from "../../state/charge-guard";
import { CHOOSE_ANOTHER_TIME } from "../../state/confirmation-outcome";
import { formatDue } from "../../state/checkout-steps";
import type { useCheckoutSubmit } from "./useCheckoutSubmit";

/** Everything useCheckoutSubmit() hands its surface. */
export type CheckoutState = ReturnType<typeof useCheckoutSubmit>;

// Curated country list for the accommodation checkout (ISO codes for the
// Cloudbeds reservation). Common source markets for SA venues; ZA default.
export const COUNTRY_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ["ZA", "South Africa"], ["GB", "United Kingdom"], ["US", "United States"],
  ["DE", "Germany"], ["NL", "Netherlands"], ["FR", "France"], ["BE", "Belgium"],
  ["CH", "Switzerland"], ["AT", "Austria"], ["IT", "Italy"], ["ES", "Spain"],
  ["PT", "Portugal"], ["IE", "Ireland"], ["SE", "Sweden"], ["NO", "Norway"],
  ["DK", "Denmark"], ["FI", "Finland"], ["PL", "Poland"], ["AU", "Australia"],
  ["NZ", "New Zealand"], ["CA", "Canada"], ["BR", "Brazil"], ["AE", "United Arab Emirates"],
  ["IN", "India"], ["CN", "China"], ["JP", "Japan"], ["NA", "Namibia"],
  ["BW", "Botswana"], ["ZW", "Zimbabwe"], ["MZ", "Mozambique"], ["KE", "Kenya"],
  ["NG", "Nigeria"], ["MU", "Mauritius"], ["Other", "Other"],
];

/**
 * "Your details": first + last name, email + mobile (two to a row where both
 * fit), the loyalty banner, and Country for a stay.
 */
export function ContactFields({ c }: { c: CheckoutState }) {
  const {
    tenant,
    contactId,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    phone,
    setPhone,
    phoneError,
    setPhoneError,
    setLoyaltySummary,
    hasAccommodation,
    country,
    setCountry,
  } = c;
  return (
    <>
      {/* Two to a row — first + last name, then email + mobile — wherever
          both fit (.vt-field-pair: two columns from 340px of form width at
          the default text size); otherwise one under the other. The DOM
          order is the reading and tab order either way. */}
      <div className="vt-field-pair">
        <div className="vt-field">
          <label htmlFor={`${contactId}-first`}>First name</label>
          <input
            id={`${contactId}-first`}
            required
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="vt-field">
          <label htmlFor={`${contactId}-last`}>Last name</label>
          <input
            id={`${contactId}-last`}
            required
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
      </div>
      <div className="vt-field-pair">
        <div className="vt-field">
          <label htmlFor={`${contactId}-email`}>Email</label>
          <input
            id={`${contactId}-email`}
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="vt-field">
          <label htmlFor={`${contactId}-phone`}>Mobile number</label>
          <input
            id={`${contactId}-phone`}
            type="tel"
            required
            autoComplete="tel"
            placeholder="+27 82 123 4567"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (phoneError) setPhoneError(null);
            }}
            onBlur={() => {
              // Normalise to international format as soon as the guest leaves
              // the field — SMS / WhatsApp confirmations need E.164.
              if (!phone.trim()) return;
              const check = normalizePhoneE164(phone);
              if (check.ok) {
                setPhone(check.e164!);
                setPhoneError(null);
              } else {
                setPhoneError(check.error ?? null);
              }
            }}
            aria-invalid={phoneError != null}
            aria-describedby={phoneError ? `${contactId}-phone-error` : undefined}
          />
          {phoneError && (
            <div id={`${contactId}-phone-error`} className="vt-field-error" role="alert">
              {phoneError}
            </div>
          )}
        </div>
      </div>
      {/* Loyalty banner — debounced lookup against the tenant's
          programme using the entered email. Self-hides if the tenant
          doesn't run a programme or the email isn't valid yet. The
          summary is lifted up so the redemption control near the cart
          total can reuse it. Under the email + mobile row, full width. */}
      <LoyaltyBanner
        tenantId={tenant.id}
        email={email}
        onSummary={setLoyaltySummary}
      />
      {hasAccommodation && (
        <div className="vt-field vt-field-chevron">
          <label htmlFor={`${contactId}-country`}>Country</label>
          <select id={`${contactId}-country`} value={country} onChange={(e) => setCountry(e.target.value)}>
            {COUNTRY_OPTIONS.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}

/** Special requests (not for a spa-only cart). */
export function SpecialRequestsField({ c }: { c: CheckoutState }) {
  const { contactId, isChideskOnly, notes, setNotes } = c;
  return (
    <>
      {!isChideskOnly && (
        <div className="vt-field">
          {/* The label names its box (it did not, before the full page). */}
          <label htmlFor={`${contactId}-notes`}>Special requests</label>
          <textarea id={`${contactId}-notes`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      )}
    </>
  );
}

/** A spa-only cart: "Who is each treatment for?" per treatment. */
export function ChideskAttribution({ c }: { c: CheckoutState }) {
  const { isChideskOnly, chideskItems, ensureContact, patchContact } = c;
  return (
    <>
      {/* ── Per-item "Who is this for?" for ChiDesk items ── */}
      {isChideskOnly && chideskItems.length > 0 && (
        <div className="vt-attribution">
          <div className="vt-checkout-section-title">Who is each treatment for?</div>
          {chideskItems.map((item) => {
            const contact = ensureContact(item.id);
            return (
              <div key={item.id} className="vt-attribution-item">
                <div className="vt-attribution-head">
                  <div>
                    <div className="vt-attribution-title">
                      {(item.metadata.treatment_name as string) ?? item.description}
                    </div>
                    <div className="vt-attribution-sub">{item.description}</div>
                  </div>
                </div>
                <label className="vt-attribution-toggle">
                  <input
                    type="checkbox"
                    checked={contact.forSomeoneElse}
                    onChange={(e) =>
                      patchContact(item.id, { forSomeoneElse: e.target.checked })
                    }
                  />
                  <span>Booking for someone else</span>
                </label>
                {contact.forSomeoneElse && (
                  <div className="vt-attribution-fields">
                    <div className="vt-field">
                      <label>First name</label>
                      <input
                        required
                        value={contact.firstName}
                        onChange={(e) =>
                          patchContact(item.id, { firstName: e.target.value })
                        }
                      />
                    </div>
                    <div className="vt-field">
                      <label>Last name</label>
                      <input
                        required
                        value={contact.lastName}
                        onChange={(e) =>
                          patchContact(item.id, { lastName: e.target.value })
                        }
                      />
                    </div>
                    <div className="vt-field">
                      <label>Email</label>
                      <input
                        type="email"
                        required
                        value={contact.email}
                        onChange={(e) =>
                          patchContact(item.id, { email: e.target.value })
                        }
                      />
                    </div>
                    <div className="vt-field">
                      <label>Mobile (optional)</label>
                      <input
                        type="tel"
                        placeholder="+27 82 123 4567"
                        value={contact.phone}
                        onChange={(e) =>
                          patchContact(item.id, {
                            phone: e.target.value,
                            phoneError: null,
                          })
                        }
                        onBlur={() => {
                          // The booker's own mobile has normalised on blur for
                          // months; this field never did, so a guest's number
                          // was only ever looked at during submit and any
                          // complaint arrived attached to the whole basket.
                          if (!contact.phone.trim()) {
                            patchContact(item.id, { phoneError: null });
                            return;
                          }
                          const check = normalizePhoneE164(contact.phone);
                          patchContact(
                            item.id,
                            check.ok
                              ? { phone: check.e164!, phoneError: null }
                              : { phoneError: check.error ?? null },
                          );
                        }}
                        aria-invalid={contact.phoneError != null}
                      />
                      {contact.phoneError && (
                        <div className="vt-field-error" role="alert">
                          {contact.phoneError}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * What is charged: the Tickets / Full payment / Deposit rows, then the spa
 * deposit and balance (a spa-only cart), or codes and gift cards, the
 * discount and "Due now".
 */
export function CheckoutTotals({ c, currency }: { c: CheckoutState; currency: string }) {
  const {
    tenant,
    isChideskOnly,
    isMixedCart,
    codesAccepted,
    ticketCents,
    requiredCents,
    depositCents,
    depositLines,
    chideskHasDeposit,
    chideskDepositCents,
    chideskTotalCents,
    firstBookingId,
    appliedCodes,
    codeInput,
    setCodeInput,
    codePending,
    codeError,
    applyCode,
    removeCode,
    email,
    loyaltySummary,
    appliedRedemption,
    setAppliedRedemption,
    discountCents,
    totalCents,
    mixedDueNowCents,
  } = c;
  return (
    <>
      {!isChideskOnly &&
        (ticketCents > 0 || requiredCents > 0 || depositCents > 0) && (
        <div className="vt-checkout-breakdown">
          {ticketCents > 0 && (
            <div className="vt-checkout-row">
              <span>Tickets</span>
              <strong>
                {currency} {(ticketCents / 100).toLocaleString()}
              </strong>
            </div>
          )}
          {requiredCents > 0 && (
            <div className="vt-checkout-row">
              <span>Full payment</span>
              <strong>
                {currency} {(requiredCents / 100).toLocaleString()}
              </strong>
            </div>
          )}
          {depositCents > 0 && (
            <div className="vt-checkout-row">
              <span>{depositLines > 1 ? "Deposits" : "Deposit"}</span>
              <strong>
                {currency} {(depositCents / 100).toLocaleString()}
              </strong>
            </div>
          )}
        </div>
      )}

      {isChideskOnly ? (
        <>
          {chideskHasDeposit && (
            <div className="vt-summary">
              <span>Deposit due now</span>
              <strong>
                {currency} {(chideskDepositCents! / 100).toLocaleString()}
              </strong>
            </div>
          )}
          {chideskTotalCents > 0 && (
            <div className="vt-summary vt-summary-minor">
              <span>
                {chideskHasDeposit ? "Balance at the venue" : "Payable at the venue"}
              </span>
              <span>
                {currency}{" "}
                {(
                  Math.max(0, chideskTotalCents - (chideskDepositCents ?? 0)) / 100
                ).toLocaleString()}
              </span>
            </div>
          )}
          <p className="vt-checkout-note">
            {chideskHasDeposit
              ? "Pay the deposit to confirm — the balance is settled at the venue."
              : "We'll reserve your spot in the venue's system straight away. Payment is handled on arrival unless the venue tells you otherwise."}
          </p>
        </>
      ) : (
        <>
          {/* Promo/gift-card codes aren't supported on mixed carts yet
              (create_mixed_payment_intent ignores them) — hide the control so
              the displayed total can never diverge from what's charged. */}
          {codesAccepted && firstBookingId && (
            <PromoCodeSection
              tenantId={tenant.id}
              bookingId={firstBookingId}
              applied={appliedCodes}
              input={codeInput}
              onInput={setCodeInput}
              pending={codePending}
              error={codeError}
              onApply={applyCode}
              onRemove={removeCode}
            />
          )}
          {/* Redemption stays OFF: redeem_loyalty_points_for_booking lost its
              anon grant in 20260802000030 (the email param is caller-supplied,
              so anyone could spend anyone's points) and carries a documented
              /100 under-credit bug. The banner above shows the balance for
              OTP-verified guests; spending returns when the RPC is rebuilt
              identity-bound, per that migration's own instructions. Rendering
              the control today = a button whose only outcome is a 401. */}
          {false && firstBookingId && (
            <RedeemPointsControl
              bookingId={firstBookingId!}
              email={email}
              summary={loyaltySummary}
              applied={appliedRedemption}
              onApplied={setAppliedRedemption}
            />
          )}
          {discountCents > 0 && (
            <div className="vt-summary vt-summary-minor">
              <span>Subtotal</span>
              <span>{formatDue(currency, totalCents)}</span>
            </div>
          )}
          {discountCents > 0 && (
            <div className="vt-summary vt-summary-discount">
              <span>Discount</span>
              <span>−{formatDue(currency, discountCents)}</span>
            </div>
          )}
          {isMixedCart && (chideskDepositCents ?? 0) > 0 && (
            <div className="vt-summary vt-summary-minor">
              <span>Spa deposit (today)</span>
              <span>
                {currency} {((chideskDepositCents ?? 0) / 100).toLocaleString()}
              </span>
            </div>
          )}
          <div className="vt-summary">
            <span>Due now</span>
            <strong>
              {formatDue(currency, isMixedCart ? mixedDueNowCents : Math.max(0, totalCents - discountCents))}
            </strong>
          </div>
          {isMixedCart && chideskTotalCents > (chideskDepositCents ?? 0) && (
            <div className="vt-summary vt-summary-minor">
              <span>Spa balance at the venue</span>
              <span>
                {currency}{" "}
                {(
                  Math.max(0, chideskTotalCents - (chideskDepositCents ?? 0)) / 100
                ).toLocaleString()}
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** The privacy / terms tick (required) and the optional marketing opt-in. */
export function CheckoutConsents({ c }: { c: CheckoutState }) {
  const { tenant, consentTerms, setConsentTerms, consentMarketing, setConsentMarketing } = c;
  return (
    <>
      <div className="vt-consents">
        <label className="vt-consent-row">
          <input
            type="checkbox"
            checked={consentTerms}
            onChange={(e) => setConsentTerms(e.target.checked)}
            required
          />
          <span>
            I have read and agree to{" "}
            <a href="/legal/privacy" target="_blank" rel="noreferrer">
              {tenant.name}&apos;s Privacy Notice
            </a>{" "}
            and to the{" "}
            <a href="/legal/guest-terms" target="_blank" rel="noreferrer">
              Vertu Guest Terms of Use
            </a>
            . My booking details are stored on secure servers in the European
            Union (Ireland), with a read replica in Switzerland (Zurich).
          </span>
        </label>
        <p className="vt-consent-note">
          We&apos;ll send booking confirmations and reminders for this booking
          by email, SMS, or WhatsApp — these are part of your booking and are
          sent whether or not you opt in below.
        </p>
        <label className="vt-consent-row">
          <input
            type="checkbox"
            checked={consentMarketing}
            onChange={(e) => setConsentMarketing(e.target.checked)}
          />
          <span>
            (Optional) I&apos;d like to receive occasional offers and news from{" "}
            {tenant.name} by email, SMS, or WhatsApp. I can unsubscribe at any
            time.
          </span>
        </label>
      </div>
    </>
  );
}

/** A required co-host ticket with no sibling booking: back to the visit to add it. */
export function CohostNotice({ c }: { c: CheckoutState }) {
  const { cohostUnmet } = c;
  return (
    <>
      {/* Co-hosting gate mirror — unpaired required ticket: point back to
          the cart to add the sibling booking (the server would reject
          the intent with 'cohost_required' anyway on block tenants). */}
      {cohostUnmet && (
        <div className="vt-cart-cohost" role="alert">
          <p className="vt-cart-cohost-text">
            {cohostNoticeText(cohostUnmet.cohost, cohostUnmet.eventName)}{" "}
            Head back to your visit to add it.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * The second-charge guard's notice (20260928090040): information beside Pay,
 * never an error. "Went through" (--ok) is shown where Pay was. "Already paid
 * for this slot on another cart" carries its own "Book another": Pay steps
 * aside, and only that button makes the second booking. "Went through" that
 * turned out to be refunded (the booking could not be kept, 20261001160010)
 * says so, and offers the way back to choose another time.
 */
export function GuardNotice({ c, onChooseAnotherTime }: { c: CheckoutState; onChooseAnotherTime?: () => void }) {
  if (!c.guard) return null;
  if (offersBookAnother(c.guard)) {
    return (
      <div className="vt-charge-guard vt-charge-guard--another">
        <p className="vt-charge-guard-text" role="status">
          {c.guard.message}
        </p>
        <button type="button" className="vt-btn-ghost vt-charge-guard-action" onClick={c.bookAnother} disabled={c.pending}>
          Book another
        </button>
      </div>
    );
  }
  if (c.guard.code === "earlier_payment_refunded") {
    return (
      <div className="vt-charge-guard vt-charge-guard--refunded">
        <p className="vt-charge-guard-text" role="status">
          {c.guard.message}
        </p>
        {!c.guard.bookingConfirmed && onChooseAnotherTime && (
          <button type="button" className="vt-btn-ghost vt-charge-guard-action" onClick={onChooseAnotherTime}>
            {CHOOSE_ANOTHER_TIME}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className={`vt-charge-guard${watchesEarlierPayment(c.guard) ? " vt-charge-guard--ok" : ""}`} role="status">
      {c.guard.message}
    </div>
  );
}

/** Pay is off: pending, a code still being checked, the hold ended, the terms unticked, a form not complete, the spa deposit unknown, an earlier payment went through, or the slot is already paid on another cart ("Book another" instead). */
export function payBlocked(c: CheckoutState): boolean {
  const { pending, codePending, holdEnded, paymentLapsed, consentTerms, participantSignature, participantsValid, isMixedCart, chideskDepositCents } =
    c;
  return (
    payBlockedByGuard(c.guard) ||
    offersBookAnother(c.guard) ||
    pending ||
    // A code being checked (typed, or the booking link's applied for the
    // guest): a Pay now would charge the total without it.
    codePending ||
    holdEnded ||
    paymentLapsed ||
    !consentTerms ||
    (!!participantSignature && !participantsValid) ||
    (isMixedCart && chideskDepositCents === null)
  );
}

/**
 * The lines under Pay: the spa deposit that failed to load, the Paystack hop,
 * or no payment at all. While the second-charge guard's notice is up (it
 * says not to pay again, to wait, or what continuing would make), the hop
 * line is not: "you'll be redirected to Paystack… come back when done"
 * would contradict it.
 */
export function PayNotes({ c }: { c: CheckoutState }) {
  const { isMixedCart, depositPreviewFailed, isChideskOnly, isFreeCart, guard } = c;
  return (
    <>
      {isMixedCart && depositPreviewFailed && (
        <p className="vt-checkout-note vt-text-center" style={{ marginTop: 8 }}>
          We couldn&rsquo;t load the spa deposit just now — please refresh to
          continue.
        </p>
      )}
      {!isChideskOnly && !isFreeCart && !guard && (
        <p className="vt-checkout-note vt-text-center">
          You&apos;ll be redirected to Paystack to enter card details. Your seats
          are held during checkout — come back to this page when done.
        </p>
      )}
      {!isChideskOnly && !isMixedCart && isFreeCart && (
        <p className="vt-checkout-note vt-text-center">
          No payment required — your booking confirms instantly.
        </p>
      )}
    </>
  );
}

/** "Have a promo code or gift card?" (a closed <details> until opened). */
export function PromoCodeSection({
  applied,
  input,
  onInput,
  onApply,
  onRemove,
  pending,
  error,
}: {
  tenantId: string;
  bookingId: string;
  applied: AppliedCode[];
  input: string;
  onInput: (s: string) => void;
  onApply: () => Promise<void> | void;
  onRemove: (code: string) => void;
  pending: boolean;
  error: string | null;
}) {
  // A code applied for the guest (from the booking link) or refused shows
  // itself: the box opens, and is never closed by the code going away.
  const sectionRef = useRef<HTMLDetailsElement | null>(null);
  const showOpen = applied.length > 0 || error !== null;
  useEffect(() => {
    if (showOpen && sectionRef.current) sectionRef.current.open = true;
  }, [showOpen]);
  return (
    <details ref={sectionRef} className="vt-promo-section">
      <summary className="vt-promo-summary">Have a promo code or gift card?</summary>
      <div className="vt-promo-body">
        {applied.map((c) => (
          <div key={c.code} className="vt-promo-applied">
            <span className="vt-promo-badge">{c.kind === "gift_card" ? "🎁" : "%"}</span>
            <span className="vt-promo-code">{c.code}</span>
            <span className="vt-promo-disc">−{(c.discount_cents / 100).toFixed(2)}</span>
            <button
              type="button"
              className="vt-promo-remove"
              onClick={() => onRemove(c.code)}
              aria-label={`Remove ${c.code}`}
            >
              ×
            </button>
          </div>
        ))}
        <div className="vt-promo-input-row">
          <input
            type="text"
            value={input}
            onChange={(e) => onInput(e.target.value.toUpperCase())}
            placeholder="WELCOME15 or VG-..."
            className="vt-promo-input"
            autoComplete="off"
          />
          <button
            type="button"
            className="vt-promo-apply"
            onClick={() => void onApply()}
            disabled={pending || !input.trim()}
          >
            {pending ? "…" : "Apply"}
          </button>
        </div>
        {error && <p className="vt-promo-error">{error}</p>}
      </div>
    </details>
  );
}
